import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Base64 } from 'js-base64';
import { JsonResult } from '../core/json-result.model';
import { AccountBasicData } from '../models/account/account-basic-data.model';
import { AccountStatement } from '../models/account/account-statement.model';
import { AccountInsertRequest } from '../models/account/account-insert-request.model';
import { Account } from '../models/account.model';
import { AccountUpdate } from '../models/account/account-update.model';
import { EnvironmentService } from '../core/services/environment.service';
import { CommonService } from './common.service';
import { takeUntil, tap } from 'rxjs/operators';
import { AccountLock } from '../models/account/account-lock.model';
import { AppConstant } from '../constants/app.constant';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  bandera: boolean = false;
  typeClient: string;
  private controller: string = "Account";
  constructor(private httpClient: HttpClient,
    private environmentService: EnvironmentService,
    private commonService: CommonService) { }

  getBasicData(clientCode: string): Observable<JsonResult<AccountBasicData>> {
    const basicData = this.getStorageBasicData();
    if (basicData != null && basicData.clientCode === clientCode) {
      return of(new JsonResult<AccountBasicData>(basicData));
    } else {
      return this.getBasicDataRequired(clientCode);
    }
  }

  getBasicDataRequired(clientCode: string): Observable<JsonResult<AccountBasicData>> {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/GetBasicData`;
    let params = new HttpParams();
    params = params.append("clientCode", clientCode)
    return this.httpClient.get<JsonResult<AccountBasicData>>(url, { params: params })
      .pipe(tap(response => {
        if (response.valid) {
          response.data.clientCode = clientCode;
          this.setStorageBasicData(response.data);
        }
      }),
        takeUntil(this.commonService.unauthorizedAnnounced));
  }

  getAccountStatement(clientCode: string): Observable<JsonResult<AccountStatement>> {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/GetAccountStatement`;
    let params = new HttpParams();
    params = params.append("clientCode", clientCode)
    return this.httpClient.get<JsonResult<AccountStatement>>(url, { params: params }).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }
  getBanderaReclamos() {
    this.bandera = true;
  }
  getBandera() {
    return this.bandera;
  }
  setTypeClient(type: string) {
    this.typeClient = type;
  }
  getTypeClient() {
    return this.typeClient;
  }
  add(accountInsertRequest: AccountInsertRequest) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}`;
    return this.httpClient.post<JsonResult<any>>(url, accountInsertRequest);
  }
  delete(clientCode: string) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}`;
    let params = new HttpParams();
    params = params.append("clientCode", clientCode)
    return this.httpClient.delete<JsonResult<any>>(url, { params: params });
  }
  list() {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/List`;
    return this.httpClient.get<JsonResult<Account[]>>(url).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  listExternal(token: string = '') {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/List-external`;
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'X-SKIP-REPLACE-TOKEN': 'X-SKIP-REPLACE-TOKEN'
    })
    return this.httpClient.get<JsonResult<Account[]>>(url, { headers }).pipe(takeUntil(this.commonService.unauthorizedAnnounced));
  }

  updateBasicData(data: AccountUpdate) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/UpdateBasicData`;
    return this.httpClient.post<JsonResult<any>>(url, data);
  }
  reconnectService(data: AccountUpdate) {
    let url = `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/ReconnectService`;
    return this.httpClient.post<JsonResult<any>>(url, data);
  }
  accountLock(data: any): Observable<JsonResult<AccountLock>> {

    let httpParams = new HttpParams();
    httpParams = httpParams.append("idUser", data.idUser);
    httpParams = httpParams.append("clientCode", data.clientCode);
    httpParams = httpParams.append("interlocutor", data.interlocutorId);

    if (data.acceptCommerce != null) {
      httpParams = httpParams.append("acceptcommerce", data.acceptCommerce);
    }
    let url =
      `${this.environmentService.virtualOfficeServiceUrl}${this.controller}/GetLockAccount`;
    return this.httpClient.get<JsonResult<AccountLock>>(url, { params: httpParams });
  }

  setStorageBasicData(accountBasicData: AccountBasicData) {
    if (this.environmentService.encryptEnabled) {
      let data = JSON.stringify(accountBasicData);
      let dataEncrypted = Base64.encode(data);
      localStorage.setItem(AppConstant.accountBasicData, dataEncrypted);
    } else {
      localStorage.setItem(AppConstant.accountBasicData, JSON.stringify(accountBasicData));
    }
  }
  getStorageBasicData(): AccountBasicData {
    if (this.environmentService.encryptEnabled) {
      let dataEncrypted = localStorage.getItem(AppConstant.accountBasicData);
      if (dataEncrypted != null) {
        let dataDecrypted = Base64.decode(dataEncrypted);
        return JSON.parse(dataDecrypted) as AccountBasicData;
      }
      return null;
    } else {
      return JSON.parse(localStorage.getItem(AppConstant.accountBasicData)) as AccountBasicData;
    }
  }
  deleteStorageBasicData() {
    localStorage.removeItem(AppConstant.accountBasicData);
  }
}
